import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';

// ── Constants ────────────────────────────────────────────────────────────────

const LAYTIME_TERMS = [
  { value: 'SHINC', label: 'SHINC', desc: 'Sundays & Holidays Included' },
  { value: 'SHEX',  label: 'SHEX',  desc: 'Sundays & Holidays Excluded' },
  { value: 'SHEXUU',label: 'SHEXUU',desc: 'SHEX Unless Used' },
  { value: 'WWD',   label: 'WWD',   desc: 'Weather Working Day' },
  { value: 'CQD',   label: 'CQD',   desc: 'Customary Quick Dispatch' },
  { value: 'WIBON', label: 'WIBON', desc: 'Whether In Berth Or Not' },
  { value: 'WIPON', label: 'WIPON', desc: 'Whether In Port Or Not' },
];

const STATUS_META = {
  draft:      { label: 'Entwurf',       color: 'var(--text3)' },
  calculated: { label: 'Kalkuliert',    color: '#3b82f6' },
  confirmed:  { label: 'Bestätigt',     color: '#22c55e' },
  disputed:   { label: 'Strittig',      color: '#ef4444' },
  closed:     { label: 'Abgeschlossen', color: 'var(--text3)' },
};

const DISPUTE_META = {
  none:         { label: 'Kein Streit',    color: '#22c55e' },
  raised:       { label: 'Eingereicht',    color: '#f59e0b' },
  under_review: { label: 'In Prüfung',     color: '#f97316' },
  settled:      { label: 'Einigung',       color: '#22c55e' },
  arbitration:  { label: 'Schiedsgericht', color: '#ef4444' },
  closed:       { label: 'Abgeschlossen',  color: 'var(--text3)' },
};

const EMPTY_FORM = {
  deal_reference: '', title: '',
  vessel_name: '', voyage_number: '',
  port_of_discharge: '', port_of_loading: '',
  nor_tendered_date: '', nor_accepted_date: '',
  berthing_date: '', cargo_commenced_date: '',
  cargo_completed_date: '', documents_released_date: '',
  laytime_terms: 'SHINC',
  laytime_allowed_hours: '', laytime_used_hours: '',
  laytime_balance_hours: '', laytime_notes: '',
  demurrage_rate: '', demurrage_hours: '', demurrage_amount: '',
  currency: 'USD',
  despatch_rate: '', despatch_hours: '', despatch_amount: '',
  dispute_status: 'none', dispute_raised_date: '',
  dispute_reason: '', dispute_notes: '',
  counter_claim_amount: '', settlement_amount: '', resolution_date: '',
  charterer_name: '', owner_name: '', agent_name: '',
  status: 'draft', notes: '', tags: [],
};

// ── Badge ────────────────────────────────────────────────────────────────────

function Badge({ meta, value }) {
  const m = meta[value] || { label: value, color: 'var(--text3)' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 600,
      background: m.color + '20', border: `1px solid ${m.color}40`, color: m.color,
    }}>{m.label}</span>
  );
}

// ── Field ────────────────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 10px',
  background: '#0f1a0f', border: '1px solid var(--border)', color: 'var(--text1)',
  borderRadius: 6, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit',
};

const selectStyle = { ...inputStyle };

// ── Main Component ────────────────────────────────────────────────────────────

export default function DemurrageCalculatorPage() {
  const [records, setRecords]   = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [tab, setTab]           = useState('port');
  const [filterStatus, setFilterStatus]   = useState('');
  const [filterDispute, setFilterDispute] = useState('');
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterStatus)  params.status = filterStatus;
      if (filterDispute) params.dispute_status = filterDispute;
      const [listRes, sumRes] = await Promise.all([
        API.get('/demurrage/', { params }),
        API.get('/demurrage/summary'),
      ]);
      setRecords(listRes.data);
      setSummary(sumRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterDispute]);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditId(null);
    setTab('port');
    setShowForm(true);
    setDetailId(null);
  }

  function openEdit(r) {
    setForm({
      deal_reference: r.deal_reference || '',
      title: r.title || '',
      vessel_name: r.vessel_name || '',
      voyage_number: r.voyage_number || '',
      port_of_discharge: r.port_of_discharge || '',
      port_of_loading: r.port_of_loading || '',
      nor_tendered_date: r.nor_tendered_date || '',
      nor_accepted_date: r.nor_accepted_date || '',
      berthing_date: r.berthing_date || '',
      cargo_commenced_date: r.cargo_commenced_date || '',
      cargo_completed_date: r.cargo_completed_date || '',
      documents_released_date: r.documents_released_date || '',
      laytime_terms: r.laytime_terms || 'SHINC',
      laytime_allowed_hours: r.laytime_allowed_hours || '',
      laytime_used_hours: r.laytime_used_hours || '',
      laytime_balance_hours: r.laytime_balance_hours || '',
      laytime_notes: r.laytime_notes || '',
      demurrage_rate: r.demurrage_rate || '',
      demurrage_hours: r.demurrage_hours || '',
      demurrage_amount: r.demurrage_amount || '',
      currency: r.currency || 'USD',
      despatch_rate: r.despatch_rate || '',
      despatch_hours: r.despatch_hours || '',
      despatch_amount: r.despatch_amount || '',
      dispute_status: r.dispute_status || 'none',
      dispute_raised_date: r.dispute_raised_date || '',
      dispute_reason: r.dispute_reason || '',
      dispute_notes: r.dispute_notes || '',
      counter_claim_amount: r.counter_claim_amount || '',
      settlement_amount: r.settlement_amount || '',
      resolution_date: r.resolution_date || '',
      charterer_name: r.charterer_name || '',
      owner_name: r.owner_name || '',
      agent_name: r.agent_name || '',
      status: r.status || 'draft',
      notes: r.notes || '',
      tags: r.tags || [],
    });
    setEditId(r.id);
    setTab('port');
    setShowForm(true);
    setDetailId(null);
  }

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function save() {
    if (!form.title.trim()) { setError('Titel ist Pflichtfeld'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (editId) {
        await API.patch(`/demurrage/${editId}`, payload);
      } else {
        await API.post('/demurrage/', payload);
      }
      setShowForm(false);
      setEditId(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function del(id) {
    if (!window.confirm('Kalkulation löschen?')) return;
    try {
      await API.delete(`/demurrage/${id}`);
      if (detailId === id) setDetailId(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }

  function calcBalance() {
    const allowed = parseFloat(form.laytime_allowed_hours);
    const used    = parseFloat(form.laytime_used_hours);
    if (!isNaN(allowed) && !isNaN(used)) {
      set('laytime_balance_hours', (allowed - used).toFixed(2));
    }
  }

  function calcDemurrage() {
    const rate  = parseFloat(form.demurrage_rate);
    const hours = parseFloat(form.demurrage_hours);
    if (!isNaN(rate) && !isNaN(hours) && hours > 0) {
      set('demurrage_amount', ((rate / 24) * hours).toFixed(2));
    }
  }

  function calcDespatch() {
    const rate  = parseFloat(form.despatch_rate);
    const hours = parseFloat(form.despatch_hours);
    if (!isNaN(rate) && !isNaN(hours) && hours > 0) {
      set('despatch_amount', ((rate / 24) * hours).toFixed(2));
    }
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text1)' }}>⚓ Demurrage Calculator</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text3)' }}>
            Hafenverzögerungen · Liegezeit-Kalkulation · Demurrage-Kosten · Streitdokumentation
          </p>
        </div>
        <button onClick={openCreate} style={{
          background: '#22c55e', color: '#000', border: 'none',
          borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}>+ Neue Kalkulation</button>
      </div>

      {error && (
        <div style={{ background: '#ef444415', border: '1px solid #ef444440', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#ef4444', fontSize: 13 }}>
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Kalkulationen',  value: summary.total, icon: '📊', color: '#3b82f6' },
            { label: 'Demurrage ges.', value: `${summary.currency || 'USD'} ${(summary.total_demurrage || 0).toLocaleString()}`, icon: '⏰', color: '#ef4444' },
            { label: 'Despatch ges.',  value: `${summary.currency || 'USD'} ${(summary.total_despatch || 0).toLocaleString()}`,  icon: '✅', color: '#22c55e' },
            { label: 'Netto-Exposure', value: `${summary.currency || 'USD'} ${(summary.net_exposure || 0).toLocaleString()}`,   icon: '⚖️', color: (summary.net_exposure || 0) > 0 ? '#ef4444' : '#22c55e' },
            { label: 'Strittig',       value: (summary.by_dispute?.raised || 0) + (summary.by_dispute?.under_review || 0) + (summary.by_dispute?.arbitration || 0), icon: '⚠️', color: '#f97316' },
          ].map(c => (
            <div key={c.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 20 }}>{c.icon}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: c.color, margin: '4px 0 2px' }}>{c.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...selectStyle, width: 'auto', minWidth: 150 }}>
          <option value="">Alle Status</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterDispute} onChange={e => setFilterDispute(e.target.value)} style={{ ...selectStyle, width: 'auto', minWidth: 170 }}>
          <option value="">Alle Streitstatus</option>
          {Object.entries(DISPUTE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={load} style={{
          padding: '8px 14px', borderRadius: 6, border: '1px solid var(--border)',
          background: 'var(--bg-card)', color: 'var(--text2)', cursor: 'pointer', fontSize: 13,
        }}>
          🔄 Aktualisieren
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1000, overflowY: 'auto', padding: '20px 0',
        }}>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 700, margin: '0 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text1)' }}>
                {editId ? 'Kalkulation bearbeiten' : 'Neue Demurrage-Kalkulation'}
              </h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text3)' }}>✕</button>
            </div>

            {error && (
              <div style={{ background: '#ef444415', border: '1px solid #ef444440', borderRadius: 6, padding: '8px 12px', marginBottom: 12, color: '#ef4444', fontSize: 13 }}>
                {error}
              </div>
            )}

            {/* Base fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <Field label="Titel *">
                <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} placeholder="z.B. MOH Vessel Delay – März 2026" />
              </Field>
              <Field label="Deal-Referenz">
                <input style={inputStyle} value={form.deal_reference} onChange={e => set('deal_reference', e.target.value)} placeholder="z.B. MOH-2026-001" />
              </Field>
              <Field label="Schiff">
                <input style={inputStyle} value={form.vessel_name} onChange={e => set('vessel_name', e.target.value)} placeholder="Vessel Name" />
              </Field>
              <Field label="Voyage Nr.">
                <input style={inputStyle} value={form.voyage_number} onChange={e => set('voyage_number', e.target.value)} placeholder="z.B. V001" />
              </Field>
              <Field label="Ladehafen">
                <input style={inputStyle} value={form.port_of_loading} onChange={e => set('port_of_loading', e.target.value)} placeholder="z.B. Bonny Terminal, Nigeria" />
              </Field>
              <Field label="Löschhafen">
                <input style={inputStyle} value={form.port_of_discharge} onChange={e => set('port_of_discharge', e.target.value)} placeholder="z.B. Agioi Theodoroi, Greece" />
              </Field>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid var(--border)', marginBottom: 16 }}>
              {[
                { key: 'port',    label: '🚢 Hafenverzögerung' },
                { key: 'laytime', label: '⏱ Liegezeit' },
                { key: 'costs',   label: '💰 Kosten' },
                { key: 'dispute', label: '⚖️ Streit' },
                { key: 'parties', label: '👥 Parteien' },
              ].map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{
                  padding: '8px 12px', border: 'none',
                  borderBottom: tab === t.key ? '2px solid #22c55e' : '2px solid transparent',
                  background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  color: tab === t.key ? '#22c55e' : 'var(--text3)', marginBottom: -2,
                }}>{t.label}</button>
              ))}
            </div>

            {/* Tab: Port Delays */}
            {tab === 'port' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label="NOR Tendered (Notice of Readiness)">
                  <input type="date" style={inputStyle} value={form.nor_tendered_date} onChange={e => set('nor_tendered_date', e.target.value)} />
                </Field>
                <Field label="NOR Accepted">
                  <input type="date" style={inputStyle} value={form.nor_accepted_date} onChange={e => set('nor_accepted_date', e.target.value)} />
                </Field>
                <Field label="Festgemacht (Berthing)">
                  <input type="date" style={inputStyle} value={form.berthing_date} onChange={e => set('berthing_date', e.target.value)} />
                </Field>
                <Field label="Ladebeginn">
                  <input type="date" style={inputStyle} value={form.cargo_commenced_date} onChange={e => set('cargo_commenced_date', e.target.value)} />
                </Field>
                <Field label="Ladeende">
                  <input type="date" style={inputStyle} value={form.cargo_completed_date} onChange={e => set('cargo_completed_date', e.target.value)} />
                </Field>
                <Field label="Dokumente freigegeben">
                  <input type="date" style={inputStyle} value={form.documents_released_date} onChange={e => set('documents_released_date', e.target.value)} />
                </Field>
              </div>
            )}

            {/* Tab: Laytime */}
            {tab === 'laytime' && (
              <div>
                <Field label="Liegezeit-Bedingungen">
                  <select style={selectStyle} value={form.laytime_terms} onChange={e => set('laytime_terms', e.target.value)}>
                    {LAYTIME_TERMS.map(t => (
                      <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>
                    ))}
                  </select>
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <Field label="Erlaubte Stunden">
                    <input style={inputStyle} type="number" step="0.5" value={form.laytime_allowed_hours}
                      onChange={e => set('laytime_allowed_hours', e.target.value)}
                      onBlur={calcBalance} placeholder="z.B. 72" />
                  </Field>
                  <Field label="Genutzte Stunden">
                    <input style={inputStyle} type="number" step="0.5" value={form.laytime_used_hours}
                      onChange={e => set('laytime_used_hours', e.target.value)}
                      onBlur={calcBalance} placeholder="z.B. 84" />
                  </Field>
                  <Field label="Saldo (neg=Demurrage)">
                    <input style={{ ...inputStyle, background: 'var(--bg-app)' }} value={form.laytime_balance_hours}
                      onChange={e => set('laytime_balance_hours', e.target.value)}
                      placeholder="Auto-berechnet" />
                  </Field>
                </div>
                <button onClick={calcBalance} style={{
                  padding: '6px 14px', borderRadius: 6, border: '1px solid #22c55e40',
                  background: '#22c55e15', color: '#22c55e', cursor: 'pointer', fontSize: 12, marginBottom: 10,
                }}>⟳ Saldo berechnen</button>
                <Field label="Liegezeit Notizen">
                  <textarea style={{ ...inputStyle, height: 60, resize: 'vertical' }} value={form.laytime_notes}
                    onChange={e => set('laytime_notes', e.target.value)} placeholder="Ausnahmen, Wetterverzögerungen, etc." />
                </Field>
              </div>
            )}

            {/* Tab: Costs */}
            {tab === 'costs' && (
              <div>
                <Field label="Währung">
                  <select style={selectStyle} value={form.currency} onChange={e => set('currency', e.target.value)}>
                    {['USD', 'EUR', 'GBP', 'CHF'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <div style={{ background: '#ef444410', border: '1px solid #ef444430', borderRadius: 8, padding: 14, marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#ef4444', marginBottom: 8 }}>⏰ Demurrage (Überschreitung)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <Field label="Rate (pro Tag)">
                      <input style={inputStyle} type="number" step="100" value={form.demurrage_rate}
                        onChange={e => set('demurrage_rate', e.target.value)}
                        onBlur={calcDemurrage} placeholder="z.B. 12000" />
                    </Field>
                    <Field label="Stunden">
                      <input style={inputStyle} type="number" step="0.5" value={form.demurrage_hours}
                        onChange={e => set('demurrage_hours', e.target.value)}
                        onBlur={calcDemurrage} placeholder="z.B. 24" />
                    </Field>
                    <Field label="Betrag">
                      <input style={{ ...inputStyle, background: 'var(--bg-app)' }} value={form.demurrage_amount}
                        onChange={e => set('demurrage_amount', e.target.value)}
                        placeholder="Auto-berechnet" />
                    </Field>
                  </div>
                  <button onClick={calcDemurrage} style={{
                    padding: '6px 14px', borderRadius: 6, border: '1px solid #ef444440',
                    background: '#ef444415', color: '#ef4444', cursor: 'pointer', fontSize: 12,
                  }}>⟳ Demurrage berechnen</button>
                </div>
                <div style={{ background: '#22c55e10', border: '1px solid #22c55e30', borderRadius: 8, padding: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#22c55e', marginBottom: 8 }}>✅ Despatch (Frühzeitige Fertigstellung)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <Field label="Rate (pro Tag)">
                      <input style={inputStyle} type="number" step="100" value={form.despatch_rate}
                        onChange={e => set('despatch_rate', e.target.value)}
                        onBlur={calcDespatch} placeholder="z.B. 6000" />
                    </Field>
                    <Field label="Stunden">
                      <input style={inputStyle} type="number" step="0.5" value={form.despatch_hours}
                        onChange={e => set('despatch_hours', e.target.value)}
                        onBlur={calcDespatch} placeholder="z.B. 12" />
                    </Field>
                    <Field label="Betrag">
                      <input style={{ ...inputStyle, background: 'var(--bg-app)' }} value={form.despatch_amount}
                        onChange={e => set('despatch_amount', e.target.value)}
                        placeholder="Auto-berechnet" />
                    </Field>
                  </div>
                  <button onClick={calcDespatch} style={{
                    padding: '6px 14px', borderRadius: 6, border: '1px solid #22c55e40',
                    background: '#22c55e15', color: '#22c55e', cursor: 'pointer', fontSize: 12,
                  }}>⟳ Despatch berechnen</button>
                </div>
              </div>
            )}

            {/* Tab: Dispute */}
            {tab === 'dispute' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Field label="Streitstatus">
                    <select style={selectStyle} value={form.dispute_status} onChange={e => set('dispute_status', e.target.value)}>
                      {Object.entries(DISPUTE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Streit eingereicht am">
                    <input type="date" style={inputStyle} value={form.dispute_raised_date} onChange={e => set('dispute_raised_date', e.target.value)} />
                  </Field>
                </div>
                <Field label="Streitgrund">
                  <textarea style={{ ...inputStyle, height: 60, resize: 'vertical' }} value={form.dispute_reason}
                    onChange={e => set('dispute_reason', e.target.value)} placeholder="Begründung des Disputes..." />
                </Field>
                <Field label="Dispute-Notizen">
                  <textarea style={{ ...inputStyle, height: 60, resize: 'vertical' }} value={form.dispute_notes}
                    onChange={e => set('dispute_notes', e.target.value)} placeholder="Zeitplan, Schriftverkehr, etc." />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <Field label="Gegenforderung">
                    <input style={inputStyle} value={form.counter_claim_amount} onChange={e => set('counter_claim_amount', e.target.value)} placeholder="0.00" />
                  </Field>
                  <Field label="Einigungsbetrag">
                    <input style={inputStyle} value={form.settlement_amount} onChange={e => set('settlement_amount', e.target.value)} placeholder="0.00" />
                  </Field>
                  <Field label="Lösungsdatum">
                    <input type="date" style={inputStyle} value={form.resolution_date} onChange={e => set('resolution_date', e.target.value)} />
                  </Field>
                </div>
              </div>
            )}

            {/* Tab: Parties */}
            {tab === 'parties' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <Field label="Befrachter (Charterer)">
                    <input style={inputStyle} value={form.charterer_name} onChange={e => set('charterer_name', e.target.value)} placeholder="z.B. MOH Greece" />
                  </Field>
                  <Field label="Reeder (Owner)">
                    <input style={inputStyle} value={form.owner_name} onChange={e => set('owner_name', e.target.value)} placeholder="Ship Owner" />
                  </Field>
                  <Field label="Agent">
                    <input style={inputStyle} value={form.agent_name} onChange={e => set('agent_name', e.target.value)} placeholder="Port Agent" />
                  </Field>
                </div>
                <Field label="Status">
                  <select style={selectStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                    {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </Field>
                <Field label="Notizen">
                  <textarea style={{ ...inputStyle, height: 60, resize: 'vertical' }} value={form.notes}
                    onChange={e => set('notes', e.target.value)} placeholder="Allgemeine Notizen..." />
                </Field>
              </div>
            )}

            {/* Form actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowForm(false)} style={{
                padding: '10px 18px', border: '1px solid var(--border)', borderRadius: 8,
                background: 'var(--bg-card)', color: 'var(--text2)', cursor: 'pointer', fontSize: 14,
              }}>Abbrechen</button>
              <button onClick={save} disabled={saving} style={{
                padding: '10px 18px', border: 'none', borderRadius: 8,
                background: saving ? '#16a34a' : '#22c55e', color: '#000',
                cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
              }}>{saving ? 'Speichert…' : (editId ? 'Speichern' : 'Erstellen')}</button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Lade Kalkulationen…</div>
      ) : records.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)', background: 'var(--bg-card)', borderRadius: 10, border: '1px dashed var(--border)' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⚓</div>
          <div style={{ fontWeight: 600, color: 'var(--text2)' }}>Keine Demurrage-Kalkulationen</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Klicken Sie auf „+ Neue Kalkulation" um zu beginnen.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {records.map(r => (
            <div key={r.id} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
              padding: '14px 16px', cursor: 'pointer',
              borderLeft: `4px solid ${r.dispute_status !== 'none' ? '#ef4444' : '#22c55e'}`,
            }} onClick={() => setDetailId(detailId === r.id ? null : r.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text1)' }}>{r.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                    {r.vessel_name && `🚢 ${r.vessel_name}`}
                    {r.port_of_loading && ` · ${r.port_of_loading}`}
                    {r.port_of_discharge && ` → ${r.port_of_discharge}`}
                    {r.deal_reference && ` · ${r.deal_reference}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge meta={STATUS_META} value={r.status} />
                  {r.dispute_status !== 'none' && <Badge meta={DISPUTE_META} value={r.dispute_status} />}
                  <button onClick={e => { e.stopPropagation(); openEdit(r); }} style={{
                    padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 6,
                    background: '#0f1a0f', color: 'var(--text2)', cursor: 'pointer', fontSize: 12,
                  }}>✏️ Bearbeiten</button>
                  <button onClick={e => { e.stopPropagation(); del(r.id); }} style={{
                    padding: '4px 10px', border: '1px solid #ef444440', borderRadius: 6,
                    background: '#ef444415', color: '#ef4444', cursor: 'pointer', fontSize: 12,
                  }}>🗑 Löschen</button>
                </div>
              </div>

              {/* Key numbers inline */}
              <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                {r.laytime_terms && (
                  <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                    <strong>Liegezeit:</strong> {r.laytime_terms}
                    {r.laytime_allowed_hours && ` · Erlaubt: ${r.laytime_allowed_hours}h`}
                    {r.laytime_used_hours && ` · Genutzt: ${r.laytime_used_hours}h`}
                    {r.laytime_balance_hours && ` · Saldo: ${r.laytime_balance_hours}h`}
                  </span>
                )}
                {r.demurrage_amount && (
                  <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                    Demurrage: {r.currency} {parseFloat(r.demurrage_amount).toLocaleString()}
                  </span>
                )}
                {r.despatch_amount && (
                  <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>
                    Despatch: {r.currency} {parseFloat(r.despatch_amount).toLocaleString()}
                  </span>
                )}
              </div>

              {/* Detail expand */}
              {detailId === r.id && (
                <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                    {/* Port Timeline */}
                    <div style={{ background: '#0f1a0f', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>🚢 Hafenzeitplan</div>
                      {[
                        { label: 'NOR Tendered', value: r.nor_tendered_date },
                        { label: 'NOR Accepted', value: r.nor_accepted_date },
                        { label: 'Festgemacht',  value: r.berthing_date },
                        { label: 'Ladebeginn',   value: r.cargo_commenced_date },
                        { label: 'Ladeende',     value: r.cargo_completed_date },
                        { label: 'Dok. freigegeben', value: r.documents_released_date },
                      ].filter(x => x.value).map(x => (
                        <div key={x.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                          <span style={{ color: 'var(--text3)' }}>{x.label}</span>
                          <span style={{ fontWeight: 600, color: 'var(--text1)' }}>{x.value}</span>
                        </div>
                      ))}
                    </div>
                    {/* Dispute */}
                    {r.dispute_status !== 'none' && (
                      <div style={{ background: '#ef444410', border: '1px solid #ef444430', borderRadius: 8, padding: 12 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: '#ef4444', marginBottom: 8 }}>⚖️ Streit</div>
                        {r.dispute_raised_date && <div style={{ fontSize: 12, marginBottom: 3, color: 'var(--text1)' }}>Eingereicht: <strong>{r.dispute_raised_date}</strong></div>}
                        {r.dispute_reason && <div style={{ fontSize: 12, marginBottom: 3, color: 'var(--text2)' }}>{r.dispute_reason}</div>}
                        {r.counter_claim_amount && <div style={{ fontSize: 12, marginBottom: 3, color: 'var(--text1)' }}>Gegenforderung: <strong>{r.currency} {r.counter_claim_amount}</strong></div>}
                        {r.settlement_amount && <div style={{ fontSize: 12, marginBottom: 3, color: '#22c55e' }}>Einigung: <strong>{r.currency} {r.settlement_amount}</strong></div>}
                        {r.resolution_date && <div style={{ fontSize: 12, color: 'var(--text1)' }}>Gelöst: <strong>{r.resolution_date}</strong></div>}
                      </div>
                    )}
                    {/* Parties */}
                    {(r.charterer_name || r.owner_name || r.agent_name) && (
                      <div style={{ background: '#0f1a0f', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>👥 Parteien</div>
                        {r.charterer_name && <div style={{ fontSize: 12, marginBottom: 3, color: 'var(--text1)' }}>Befrachter: <strong>{r.charterer_name}</strong></div>}
                        {r.owner_name && <div style={{ fontSize: 12, marginBottom: 3, color: 'var(--text1)' }}>Reeder: <strong>{r.owner_name}</strong></div>}
                        {r.agent_name && <div style={{ fontSize: 12, marginBottom: 3, color: 'var(--text1)' }}>Agent: <strong>{r.agent_name}</strong></div>}
                      </div>
                    )}
                  </div>
                  {r.notes && (
                    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>{r.notes}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
